/**
 * File: web/src/lib/auth.ts
 * ========================
 *
 * Cognito authentication helper (Hosted UI + PKCE)
 *
 * Purpose:
 * - Implement OAuth2 Authorization Code + PKCE for a browser-based SPA.
 * - Store the user’s id_token in sessionStorage.
 * - Provide helpers for login, callback handling (code exchange), and logout.
 *
 * When used:
 * - login(): start the Hosted UI login flow
 * - handleCallback(): exchange the code for tokens after redirect to /callback
 * - logout(): end both local session and Hosted UI session
 *
 * Learner notes:
 * - PKCE is the best practice for "public clients" (SPAs) because it doesn't require
 *   a client secret in the browser.
 * - Tokens are stored in sessionStorage to reduce persistence on shared machines.
 *
 * Gotchas:
 * - redirect_uri must match Cognito app client callback URLs exactly.
 * - If the tab is refreshed mid-login, the stored PKCE verifier may be lost.
 */

import type { AppConfig } from "./types";

const TOKEN_KEY = "id_token";
const VERIFIER_KEY = "pkce_verifier";
const POST_LOGIN_PATH = "post_login_path";
const AUTH_CHANGED_EVENT = "auth-changed";

/**
 * Token accessors
 * - Keep token storage consistent across the app.
 * - Dispatch an event so hooks (useAuthGuard) can react immediately.
 */
export function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function clearToken() {
  sessionStorage.removeItem(TOKEN_KEY);

  // Tell the rest of the app "auth state changed" (so timers can be re-armed).
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

export function setToken(t: string) {
  sessionStorage.setItem(TOKEN_KEY, t);

  // Let listeners know a new token exists.
  window.dispatchEvent(new Event(AUTH_CHANGED_EVENT));
}

/**
 * Create a PKCE verifier.
 *
 * Why crypto.getRandomValues:
 * - Verifiers must be unpredictable.
 * - This uses the browser's cryptographically secure RNG.
 */
function randomHex(len = 64) {
  const arr = new Uint8Array(len);
  crypto.getRandomValues(arr);
  return Array.from(arr)
    .map((b) => ("0" + b.toString(16)).slice(-2))
    .join("");
}

/**
 * Convert bytes into base64url format.
 *
 * PKCE requires base64url encoding (URL-safe base64 without padding).
 */
function base64url(buf: ArrayBuffer) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

/**
 * Hash a string to SHA-256 using the Web Crypto API.
 * Used to turn the verifier into the PKCE "challenge".
 */
async function sha256(str: string) {
  return crypto.subtle.digest("SHA-256", new TextEncoder().encode(str));
}

/**
 * Begin the Hosted UI login redirect.
 *
 * redirectPath:
 * - We store where the user was (e.g., /admin) so the callback route can navigate back.
 */
export async function login(config: AppConfig, redirectPath = "/") {
  // Where to go after successful login.
  sessionStorage.setItem(POST_LOGIN_PATH, redirectPath);

  // PKCE: generate verifier and derived challenge.
  const verifier = randomHex(64);
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  const challenge = base64url(await sha256(verifier));

  // Hosted UI authorize endpoint.
  const authorize = `${config.cognitoDomain}/oauth2/authorize`;

  // OAuth parameters:
  // - response_type=code => authorization code flow
  // - scope includes openid+email so id_token contains identity info
  const params = new URLSearchParams({
    client_id: config.cognitoClientId,
    response_type: "code",
    scope: "openid email",

    // Must match Cognito app client callback URL configuration.
    redirect_uri: `${window.location.origin}/callback`,

    // PKCE
    code_challenge_method: "S256",
    code_challenge: challenge,
  });

  // Navigate away from the SPA to Cognito Hosted UI.
  window.location.href = `${authorize}?${params.toString()}`;
}

/**
 * Callback handler:
 * Exchange the auth code for tokens and store id_token.
 *
 * Flow:
 * 1) Read the verifier we generated during login()
 * 2) Call /oauth2/token with code + verifier
 * 3) Store id_token and clean up temporary session storage keys
 * 4) Return the post-login destination path for the router to navigate to
 */
export async function handleCallback(
  config: AppConfig,
  code: string,
): Promise<string> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);

  // If missing, the user likely refreshed or opened callback in a new tab.
  // The safe recovery is "try signing in again".
  if (!verifier)
    throw new Error("Missing PKCE verifier. Try signing in again.");

  const tokenEndpoint = `${config.cognitoDomain}/oauth2/token`;

  // x-www-form-urlencoded is required by the token endpoint.
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: config.cognitoClientId,
    code,
    redirect_uri: `${window.location.origin}/callback`,
    code_verifier: verifier,
  });

  const res = await fetch(tokenEndpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });

  // If token exchange fails, Cognito may return helpful text/json.
  if (!res.ok) throw new Error(await res.text());

  const data = await res.json();

  // In this app we rely on id_token for identity and group checks.
  if (!data.id_token) throw new Error("No id_token returned");

  // Store token and notify listeners.
  setToken(data.id_token);

  // Clean up one-time verifier; it shouldn't persist.
  sessionStorage.removeItem(VERIFIER_KEY);

  // Navigate back to where the user started.
  const dest = sessionStorage.getItem(POST_LOGIN_PATH) ?? "/";
  sessionStorage.removeItem(POST_LOGIN_PATH);
  return dest;
}

/**
 * Logout:
 * - Clear local token and redirect to Hosted UI logout endpoint.
 *
 * Why Hosted UI logout:
 * - Clears Cognito cookies/session so "back button" doesn't immediately re-auth.
 */
export function logout(config: AppConfig) {
  clearToken();
  const url = `${config.cognitoDomain}/logout?client_id=${encodeURIComponent(config.cognitoClientId)}&logout_uri=${encodeURIComponent(window.location.origin + "/")}`;
  window.location.href = url;
}

/**
 * Base64url decoding helper (JWT payload decoding).
 * This is used only for client-side UX decisions (like admin gating).
 */
function b64urlDecode(str: string) {
  const s = str.replace(/-/g, "+").replace(/_/g, "/");
  const padded = s + "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(padded);
  const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Determine if a user is an admin based on JWT group claim.
 *
 * Important security note:
 * - This check is for UI convenience only. The backend must enforce admin rights too.
 * - The client cannot grant itself real privileges; API Gateway + Lambdas enforce it.
 */
export function isAdmin(token: string | null): boolean {
  if (!token) return false;

  // JWT structure: header.payload.signature
  const parts = token.split(".");
  if (parts.length < 2) return false;

  try {
    const payload = JSON.parse(b64urlDecode(parts[1]));

    // Cognito group claim may be array or string depending on configuration/token type.
    const groups = payload["cognito:groups"];
    if (!groups) return false;
    const list = Array.isArray(groups) ? groups : String(groups).split(",");
    return list.map((x: any) => String(x).trim()).includes("Admins");
  } catch {
    // If parsing fails, treat as not admin (safe default).
    return false;
  }
}
